import {
  Box,
  Text,
  Flex,
  VStack,
  Progress,
  Button,
  HStack,
  Input,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { ENDPOINTS } from "utils/Constants";
import { notify } from "components/ToastNotification";
import { currentUser } from "utils/CurrentUser";
import { useNavigate } from "react-router-dom";

export default function MyLearning() {
  const [ongoingSkills, setongoingSkills] = useState([]);
  const [completedSkills, setCompletedSkills] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  // Top-level usage of `useColorModeValue`
  const cardBg = useColorModeValue("gray.50", "gray.700");
  const cardTextColor = useColorModeValue("black", "white");
  const inputBg = useColorModeValue("gray.200", "gray.600");
  const courseCardBg = useColorModeValue("gray.100", "gray.800");

  useEffect(() => {
    const fetchLearningData = async () => {
      try {
        const response = await axios.get(ENDPOINTS.MY_LEARNING, {
          headers: { Authorization: `Bearer ${currentUser.token}` },
        });

        if (!response.data.success) {
          return notify(response.data.output, "error");
        }

        setongoingSkills(response.data.data.ongoingSkills || []);
        setCompletedSkills(response.data.data.completedSkills || []);
      } catch (error) {
        notify("Failed to load learning data.", "error");
      }
    };

    fetchLearningData();
  }, []);

  const filteredOngoing = ongoingSkills.filter((course) =>
    course.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredCompleted = completedSkills.filter((course) =>
    course.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const downloadCertificate = async (skillId) => {
    try {
      const response = await axios.get(
        `${ENDPOINTS.DOWNLOAD_CERTIFICATE}${skillId}`,
        {
          headers: { Authorization: `Bearer ${currentUser.token}` },
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `JunusAI-Certificate-${skillId}.pdf`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      notify('Failed to download the certificate. Please try again.', 'error');
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} px="20px">
      <Box mb="20px">
        <Text fontSize="md" color="gray.500">
          Keep up the great work! You're making progress every day.
        </Text>
      </Box>

      <HStack mb="20px">
        <Input
          placeholder="Search your skills..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          bg={inputBg}
          color={cardTextColor}
        />
      </HStack>

      <Box bg={cardBg} p="20px" borderRadius="10px" boxShadow="md" mb="20px">
        <Text fontSize="2xl" fontWeight="bold" mb="10px" color={cardTextColor}>
          Ongoing Skills
        </Text>
        {filteredOngoing.length > 0 ? (
          <VStack align="stretch" spacing="20px">
            {filteredOngoing.map((course) => (
              <Flex
                key={course.id}
                justify="space-between"
                align="center"
                p="10px"
                bg={courseCardBg}
                borderRadius="10px"
                direction={{ base: "column", md: "row" }} // Stack vertically on mobile
                gap="4"
              >
                <Box w="full">
                  <Text fontSize="lg" fontWeight="bold" color={cardTextColor}>
                    {course.name}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {course.currentLevel}
                  </Text>
                  <Progress
                    value={course.progress}
                    size="sm"
                    colorScheme="blue"
                    mt="10px"
                  />
                  <Text fontSize="sm" color="gray.500">
                    Progress: {course.progress}%
                  </Text>
                </Box>
                <Flex
                  gap="4"
                  mt={{ base: "4", md: "0" }}
                  justify="center"
                  align="center"
                  direction={{ base: "column", md: "row" }} // Stack buttons vertically on mobile
                  w="full"
                >
                  <Button
                    variant="darkBrand"
                    size="md"
                    width={{ base: "full", md: "auto" }} // Full width on mobile
                    onClick={() =>
                      navigate(
                        `/admin/chat/${course.skillID}?currentLevel=${course.currentLevel}`
                      )
                    }
                    _hover={{ transform: "scale(1.05)" }}
                  >
                    Continue Learning
                  </Button>
                  <Button
                    colorScheme="darkBrand"
                    variant="outline"
                    size="md"
                    border="2px"
                    borderColor="blue.800"
                    width={{ base: "full", md: "auto" }} // Full width on mobile
                    onClick={() =>
                      navigate(
                        `/admin/skill-assessment/${course.skillID}?currentLevel=${course.currentLevel}`
                      )
                    }
                    _hover={{ transform: "scale(1.05)" }}
                  >
                    Take Assessment
                  </Button>
                </Flex>
              </Flex>
            ))}
          </VStack>
        ) : (
          <Text color="gray.500">No ongoing skills found.</Text>
        )}

      </Box>

      <Box bg={cardBg} p="20px" borderRadius="10px" boxShadow="md">
        <Text fontSize="2xl" fontWeight="bold" mb="10px" color={cardTextColor}>
          Completed Skills
        </Text>
        {filteredCompleted.length > 0 ? (
          <VStack align="stretch" spacing="20px">
            {filteredCompleted.map((course) => (
              <Flex
                key={course.id}
                justify="space-between"
                align="center"
                p="10px"
                bg={courseCardBg}
                borderRadius="10px"
                direction={{ base: "column", md: "row" }} // Stack vertically on mobile
                gap="4"
              >
                <Box w="full">
                  <Text fontSize="lg" fontWeight="bold" color={cardTextColor}>
                    {course.name}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {course.currentLevel}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Completed on: {course.completedAt}
                  </Text>
                </Box>
                <Button
                  variant="darkBrand"
                  size="md"
                  width={{ base: "full", md: "auto" }} // Full width on mobile
                  onClick={() => downloadCertificate(course.id)}
                  _hover={{ transform: "scale(1.05)" }}
                >
                  Download Certificate
                </Button>
              </Flex>
            ))}
          </VStack>
        ) : (
          <Text color="gray.500">No completed skills found.</Text>
        )}

      </Box>
    </Box>
  );
};

